import type { PlanEvent } from "../../../shared/types";
import { loadEvents, migrateEvent, STORAGE_KEY } from "../utils/event-loaders";
import { sortEvents } from "../utils/event-helpers";
import { getPendingEventIds, syncImportAllEvents } from "../../../lib/api/db-sync";
import { track } from "../../../lib/analytics";

let eventsCache: PlanEvent[] = loadEvents();

export function getEventsCache(): PlanEvent[] {
  return eventsCache;
}

export function setEventsCache(events: PlanEvent[]) {
  eventsCache = events;
}

export const listeners = new Set<() => void>();
export const undoStack: PlanEvent[][] = [];
export const redoStack: PlanEvent[][] = [];

export function saveHistory() {
  undoStack.push([...eventsCache]);
  if (undoStack.length > 50) undoStack.shift();
  redoStack.length = 0;
}

export function emitChange() {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(eventsCache));
  for (const listener of listeners) { try { listener(); } catch { /* ignore stale listener during teardown */ } }
}

export function resetFromServer(rawEvents: unknown[]): void {
  const serverEvents = rawEvents.map(migrateEvent);
  const serverIds = new Set(serverEvents.map((e) => e.id));
  const pendingIds = getPendingEventIds();

  // Preserve local events that are pending sync (not yet confirmed by server)
  const pendingLocal = eventsCache.filter(
    (e) => pendingIds.has(e.id) && !serverIds.has(e.id),
  );

  setEventsCache(sortEvents([...serverEvents, ...pendingLocal]));
  emitChange();
}

export function subscribe(listener: () => void) {
  listeners.add(listener);

  function handleStorageChange(event: StorageEvent) {
    if (event.key === STORAGE_KEY) {
      eventsCache = loadEvents();
      emitChange();
    }
  }

  window.addEventListener("storage", handleStorageChange);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("storage", handleStorageChange);
  };
}

export function getSnapshot(): PlanEvent[] {
  return eventsCache;
}

export function importEvents(data: unknown[]) {
  eventsCache = sortEvents(data.map(migrateEvent));
  emitChange();
}

export function undoEvent(): boolean {
  const prev = undoStack.pop();
  if (!prev) return false;
  redoStack.push([...eventsCache]);
  eventsCache = prev;
  emitChange();
  syncImportAllEvents(eventsCache);
  track("undo_used", { scope: "event" });
  return true;
}

export function redoEvent(): boolean {
  const next = redoStack.pop();
  if (!next) return false;
  undoStack.push([...eventsCache]);
  eventsCache = next;
  emitChange();
  syncImportAllEvents(eventsCache);
  track("redo_used", { scope: "event" });
  return true;
}
